import { useState } from 'preact/hooks';
import { ListBullets, DownloadSimple, Copy, Check, CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { PoolResponse } from '../../services/api.js';
import { formatCoinAmount, toCSV, downloadFile, copyToClipboard } from '../../utils/format.js';
import { useToast } from '../shared/Toast.js';

const PAGE_SIZE = 25;

interface Props {
  pool: PoolResponse | null;
}

export function PaymentsPage({ pool }: Props) {
  const coinSymbol = pool?.pool.coinSymbol ?? 'XEL';
  const coinPrice = pool ? parseFloat(pool.pool.coinPriceUsd) : 0;
  const payments = pool?.miner.payments ?? [];
  const { show: toast } = useToast();
  const [page, setPage] = useState(0);

  const totalCoins = payments.reduce((sum, p) => sum + p.amount / 1e8, 0);
  const totalUsd = totalCoins * coinPrice;

  // Client-side pagination — clamp the page in case the dataset shrank on refresh
  const pageCount = Math.max(1, Math.ceil(payments.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * PAGE_SIZE;
  const visiblePayments = payments.slice(pageStart, pageStart + PAGE_SIZE);

  const handleExportCSV = () => {
    const headers = ['Дата', 'TX Hash', `Сумма (${coinSymbol})`, 'Сумма (USD)'];
    const rows = payments.map((p) => [
      new Date(p.timestamp * 1000).toISOString(),
      p.tx,
      (p.amount / 1e8).toFixed(8),
      ((p.amount / 1e8) * coinPrice).toFixed(2),
    ]);
    const csv = toCSV(headers, rows);
    downloadFile(csv, `cryptodash-payments-${new Date().toISOString().slice(0, 10)}.csv`);
    toast('CSV файл скачан', 'success');
  };

  return (
    <>
      <div class="section-header">
        <div class="section-controls">
          {payments.length > 0 && (
            <button class="btn btn-secondary" onClick={handleExportCSV}>
              <DownloadSimple size={14} />
              <span>CSV</span>
            </button>
          )}
          {pool && (
            <div class="coin-badge">
              <span>{coinSymbol}</span>
              <span style={{ color: 'var(--lime)' }}>${coinPrice.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      <div class="data-card">
        <div class="data-card-header">
          <ListBullets size={16} />
          <span>Все транзакции ({payments.length})</span>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Дата / Время</th>
                <th>Хеш транзакции</th>
                <th>Сумма</th>
                <th>USD</th>
              </tr>
            </thead>
            <tbody>
              {payments.length > 0
                ? visiblePayments.map((p, i) => {
                    const date = new Date(p.timestamp * 1000).toLocaleString('ru-RU');
                    const amount = formatCoinAmount(p.amount);
                    const usd = ((p.amount / 1e8) * coinPrice).toFixed(2);
                    return (
                      <tr key={pageStart + i}>
                        <td>{date}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <span style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 'var(--text-xs)',
                              maxWidth: '180px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'inline-block',
                            }}>
                              {p.tx}
                            </span>
                            <CopyButton text={p.tx} />
                          </div>
                        </td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{amount}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--lime)' }}>${usd}</td>
                      </tr>
                    );
                  })
                : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: 'var(--tx-muted)', padding: 'var(--space-6)' }}>
                      {pool ? 'Нет выплат' : 'Загрузка...'}
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>

        {pageCount > 1 && (
          <div class="pager">
            <button
              class="pager-btn"
              disabled={currentPage <= 0}
              onClick={() => setPage(currentPage - 1)}
              type="button"
              aria-label="Предыдущая страница"
            >
              <CaretLeft size={12} weight="bold" />
              <span>Назад</span>
            </button>
            <span class="pager-info">
              Стр. {currentPage + 1} / {pageCount} · {payments.length} записей
            </span>
            <button
              class="pager-btn"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage(currentPage + 1)}
              type="button"
              aria-label="Следующая страница"
            >
              <span>Вперёд</span>
              <CaretRight size={12} weight="bold" />
            </button>
          </div>
        )}

        {payments.length > 0 && (
          <div class="total-summary">
            <span style={{ color: 'var(--tx-secondary)', fontSize: 'var(--text-sm)' }}>Итого выплачено:</span>
            <span style={{ color: 'var(--tx-white)', fontSize: 'var(--text-sm)' }}>
              {totalCoins.toFixed(2)} {coinSymbol} (${totalUsd.toFixed(2)})
            </span>
          </div>
        )}
      </div>
    </>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: Event) => {
    e.stopPropagation();
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button class={`copy-btn${copied ? ' copied' : ''}`} onClick={handleCopy} title="Копировать">
      {copied ? <Check size={10} /> : <Copy size={10} />}
    </button>
  );
}
